import { CrossIcon } from '@/assets/icons'
import InviteModal from '@components/InviteModal'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useTrip, useTripMembers } from '@hooks'
import { countLabel } from '@/utils/plural'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    InviteGhostCard,
    InviteGhostLabel,
    MemberCard,
    MemberCardName,
    MembersGrid,
} from './MembersPage.styles'
import { Avatar } from '@components/Avatar'
import { iconSizes } from '@/theme/tokens'
import { ErrorNote } from '@components/Notice'

const MembersPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: membersData, isLoading: membersLoading } = useTripMembers(tripId)
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

	// Set breadcrumbs - must be called before any early returns
	useBreadcrumbs(
		trip
			? [
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Members' },
				]
			: []
	)

	if (tripLoading || membersLoading) {
		return (
			<PageScroll>
				<PageLoader label="Loading members…" />
			</PageScroll>
		)
	}

	if (!trip) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorNote>Trip not found</ErrorNote>
				</PageColumn>
			</PageScroll>
		)
	}

	const members = membersData?.members || []

	return (
		<PageScroll>
			<PageColumn>
				<PageHeader
					title="Members"
					subtitle={countLabel(members.length, 'member')}
				/>

				<MembersGrid>
					{members.map(member => (
						<MemberCard key={member.id}>
							<Avatar user={member} size="xl" tone="soft" />
							<MemberCardName>
								{member.firstName} {member.lastName}
							</MemberCardName>
						</MemberCard>
					))}

					{/* The empty seat closes the grid — always there, whoever is missing. */}
					<InviteGhostCard type="button" onClick={() => setIsInviteModalOpen(true)}>
						<Avatar size="xl" fallback={<CrossIcon sx={{ fontSize: iconSizes.lg }} />} />
						<InviteGhostLabel>Invite a member</InviteGhostLabel>
					</InviteGhostCard>
				</MembersGrid>
			</PageColumn>

			<InviteModal
				isOpen={isInviteModalOpen}
				onClose={() => setIsInviteModalOpen(false)}
				tripCode={trip.code}
			/>
		</PageScroll>
	)
}

export default MembersPage
